import { useState } from "react";
import { useAllMcqs } from "@/hooks/useAllMcqs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { 
  Tags, 
  Brain, 
  Play, 
  Pause, 
  Settings, 
  FileText, 
  Target,
  CheckCircle,
  Clock,
  BarChart3
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface TaggingRule {
  id: string;
  name: string;
  condition: string;
  tags: string[];
  enabled: boolean;
  contentType: string[];
  confidence: number;
}

interface TagSuggestion {
  tag: string;
  confidence: number;
  reason: string;
  category: string;
}

export default function AutoTaggingSystem() {
  const { toast } = useToast();
  const { data: allMcqs } = useAllMcqs();
  const [isRunning, setIsRunning] = useState(false);
  const [selectedContentType, setSelectedContentType] = useState("all");
  const [progress, setProgress] = useState(0);
  const [newRuleName, setNewRuleName] = useState("");
  const [newRuleCondition, setNewRuleCondition] = useState("");
  
  const [taggingRules, setTaggingRules] = useState<TaggingRule[]>([
    {
      id: "1",
      name: "Cardiology Content",
      condition: "Contains keywords: heart, cardiac, cardiovascular",
      tags: ["cardiology", "cardiovascular-system", "heart"],
      enabled: true,
      contentType: ["mcq", "note"],
      confidence: 95
    },
    {
      id: "2", 
      name: "Pharmacology Questions",
      condition: "Contains drug names or dosages",
      tags: ["pharmacology", "medications", "dosage"],
      enabled: true,
      contentType: ["mcq"],
      confidence: 88
    },
    {
      id: "3",
      name: "High Difficulty Content",
      condition: "Difficulty level 4 or 5",
      tags: ["advanced", "challenging"],
      enabled: false,
      contentType: ["mcq", "note"],
      confidence: 92
    }
  ]);

  // Mock recent tagging results
  const recentResults = [
    {
      id: "1",
      title: "Myocardial Infarction Pathophysiology",
      type: "Note",
      suggestedTags: [
        { tag: "cardiology", confidence: 95, reason: "Contains cardiac terminology", category: "specialty" },
        { tag: "pathophysiology", confidence: 90, reason: "Discusses disease mechanisms", category: "topic" },
        { tag: "emergency", confidence: 78, reason: "Acute condition content", category: "urgency" }
      ],
      appliedTags: ["cardiology", "pathophysiology"],
      timestamp: "2 minutes ago"
    },
    {
      id: "2",
      title: "Beta Blocker Mechanism MCQ",
      type: "MCQ",
      suggestedTags: [
        { tag: "pharmacology", confidence: 92, reason: "Drug mechanism question", category: "specialty" },
        { tag: "cardiovascular", confidence: 88, reason: "Beta blockers affect CV system", category: "system" },
        { tag: "intermediate", confidence: 85, reason: "Standard medical knowledge", category: "difficulty" }
      ],
      appliedTags: ["pharmacology", "cardiovascular"],
      timestamp: "5 minutes ago"
    }
  ];

  const runAutoTagging = async () => {
    setIsRunning(true);
    setProgress(0);
    
    try {
      const { data, error } = await supabase.functions.invoke('ai-auto-tagger', {
        body: {
          contentType: selectedContentType,
          rules: taggingRules.filter(rule => rule.enabled)
        }
      });

      if (error) throw error;

      // Simulate progress
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setIsRunning(false);
            toast({
              title: "Auto-Tagging Complete",
              description: `Tagged ${data.processedCount} items with ${data.tagsApplied} new tags.`,
            });
            return 100;
          }
          return prev + 10;
        });
      }, 500);

    } catch (error) {
      console.error('Error running auto-tagging:', error);
      setIsRunning(false);
      toast({
        title: "Tagging Failed",
        description: "Failed to run auto-tagging process. Please try again.",
        variant: "destructive"
      });
    }
  };

  const stopAutoTagging = () => {
    setIsRunning(false);
    setProgress(0);
    toast({
      title: "Tagging Stopped",
      description: "Auto-tagging process has been stopped.",
    });
  };

  const toggleRule = (ruleId: string) => {
    setTaggingRules(prev => prev.map(rule => 
      rule.id === ruleId 
        ? { ...rule, enabled: !rule.enabled }
        : rule
    ));
  };

  const addTaggingRule = () => {
    if (!newRuleName || !newRuleCondition) {
      toast({
        title: "Invalid Input",
        description: "Please provide both rule name and condition.",
        variant: "destructive"
      });
      return;
    }

    const newRule: TaggingRule = {
      id: Date.now().toString(),
      name: newRuleName,
      condition: newRuleCondition,
      tags: [],
      enabled: true,
      contentType: ["mcq", "note"],
      confidence: 85
    };

    setTaggingRules(prev => [...prev, newRule]);
    setNewRuleName("");
    setNewRuleCondition("");
    
    toast({
      title: "Rule Added",
      description: "New tagging rule has been created.",
    });
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return "text-success";
    if (confidence >= 75) return "text-primary";
    if (confidence >= 60) return "text-warning";
    return "text-destructive";
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "specialty": return "default";
      case "system": return "secondary";
      case "topic": return "outline";
      case "difficulty": return "destructive";
      case "urgency": return "destructive";
      default: return "secondary";
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Auto-Tagging System</h3>
          <p className="text-sm text-muted-foreground">
            Automatically categorize and tag content using AI and custom rules
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Play className="h-4 w-4" />
                Run Auto-Tagging
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Content Type</label>
                  <Select value={selectedContentType} onValueChange={setSelectedContentType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Content ({allMcqs?.length || 0} MCQs)</SelectItem>
                      <SelectItem value="mcq">MCQ Questions ({allMcqs?.length || 0})</SelectItem>
                      <SelectItem value="note">Study Notes</SelectItem>
                      <SelectItem value="explanation">Explanations</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Active Rules</label>
                  <div className="text-2xl font-bold text-primary">
                    {taggingRules.filter(rule => rule.enabled).length}
                  </div>
                </div>
              </div>

              {isRunning && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Processing content...</span>
                    <span>{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
              )}

              <div className="flex gap-2">
                {!isRunning ? (
                  <Button onClick={runAutoTagging} className="flex items-center gap-2">
                    <Brain className="h-4 w-4" />
                    Start Auto-Tagging
                  </Button>
                ) : (
                  <Button onClick={stopAutoTagging} variant="destructive" className="flex items-center gap-2">
                    <Pause className="h-4 w-4" />
                    Stop Process
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Tagging Rules
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {taggingRules.map((rule) => (
                  <div key={rule.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{rule.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {rule.contentType.join(", ")}
                        </Badge>
                        <span className={`text-xs ${getConfidenceColor(rule.confidence)}`}>
                          {rule.confidence}%
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{rule.condition}</p>
                      <div className="flex gap-1 mt-1">
                        {rule.tags.map((tag, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <Switch
                      checked={rule.enabled}
                      onCheckedChange={() => toggleRule(rule.id)}
                    />
                  </div>
                ))}
              </div>

              <Separator />

              <div className="space-y-3">
                <h4 className="font-medium">Add New Rule</h4>
                <div className="grid grid-cols-1 gap-3">
                  <Input
                    placeholder="Rule name (e.g., Anatomy Content)"
                    value={newRuleName}
                    onChange={(e) => setNewRuleName(e.target.value)}
                  />
                  <Input
                    placeholder="Condition (e.g., Contains anatomy keywords)"
                    value={newRuleCondition}
                    onChange={(e) => setNewRuleCondition(e.target.value)}
                  />
                  <Button onClick={addTaggingRule} size="sm">
                    Add Rule
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Tagging Stats
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{allMcqs?.length.toLocaleString() || 0}</div>
                  <p className="text-xs text-muted-foreground">Total MCQs</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-success">234</div>
                  <p className="text-xs text-muted-foreground">This Week</p>
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <h5 className="text-sm font-medium">Popular Tags</h5>
                <div className="flex flex-wrap gap-1">
                  {["cardiology", "pharmacology", "anatomy", "pathology", "surgery"].map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Recent Results
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {recentResults.map((result) => (
                <div key={result.id} className="p-3 border rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-tight">
                        {result.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {result.type} • {result.timestamp}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="text-xs font-medium">Suggested Tags:</div>
                    <div className="space-y-1">
                      {result.suggestedTags.map((suggestion, index) => (
                        <div key={index} className="flex items-center justify-between text-xs">
                          <Badge variant={getCategoryColor(suggestion.category) as any} className="text-xs">
                            {suggestion.tag}
                          </Badge>
                          <span className={getConfidenceColor(suggestion.confidence)}>
                            {suggestion.confidence}%
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="text-xs font-medium">Applied:</div>
                    <div className="flex gap-1">
                      {result.appliedTags.map((tag, index) => (
                        <Badge key={index} variant="default" className="text-xs">
                          <CheckCircle className="h-2 w-2 mr-1" />
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}